/**
 * Header decoration/icon for a preference type category.
 *
 * Belongs here: header SVG or fund list UI.
 * Does not belong here: questionnaire flow.
 */
import type { ReactNode } from 'react'
import { useBottomSheet } from '../../../../../../state-management/contexts/BottomSheetContext'
import { lightImpactVibrate } from '../../../../../../utils/appBridge'
import type { PreferenceFundItem } from '../../setup/types'
import type { PortfolioFundLabel } from '../../setup/config'
import { PreferenceTypeFundListSheet } from '../sheets/PreferenceTypeFundListSheet'
import { FundListItem } from './FundListItem'

interface FundCardProps {
  fund: PreferenceFundItem
  showCountBadge?: boolean
  totalCount?: number
  className?: string
  onClick?: () => void
  sheetCategoryIcon?: ReactNode
  sheetCategoryTitle?: string
  sheetFundLabel?: PortfolioFundLabel
  sheetFundList?: PreferenceFundItem[]
}

export function FundCard({
  fund,
  showCountBadge = false,
  totalCount = 0,
  className = '',
  onClick,
  sheetCategoryIcon,
  sheetCategoryTitle,
  sheetFundLabel,
  sheetFundList
}: FundCardProps) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const hasSheetProps =
    sheetCategoryIcon != null &&
    sheetCategoryTitle != null &&
    sheetFundLabel != null &&
    sheetFundList != null

  const opensFundListSheet = hasSheetProps && (sheetFundList?.length ?? 0) > 1

  function handleClick() {
    // Only haptic when the tap does something (sheet or onClick)
    if (opensFundListSheet) {
      lightImpactVibrate()
      openBottomSheet(
        <PreferenceTypeFundListSheet
          categoryIcon={sheetCategoryIcon}
          categoryTitle={sheetCategoryTitle}
          fundLabel={sheetFundLabel}
          fundList={sheetFundList}
          onDismiss={closeBottomSheet}
        />,
        closeBottomSheet
      )
      return
    }
    if (onClick == null) return
    lightImpactVibrate()
    onClick()
  }

  const isClickable = opensFundListSheet || onClick != null

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (event) => event.key === 'Enter' && handleClick() : undefined}
      onClick={isClickable ? handleClick : undefined}
      className={`relative flex items-center rounded-[16px] bg-white px-4 py-4 min-w-0 flex-1 ${isClickable ? 'cursor-pointer' : ''} ${className}`.trim()}
    >
      <FundListItem fund={fund} className="flex-1" />
      {showCountBadge && totalCount > 0 && (
        <span
          className="absolute -top-[6px] -right-[6px] flex h-7 w-7 pb-[2px] items-center justify-center rounded-full bg-[#232323] text-[13px] font-[600] text-white shrink-0"
          aria-label={`${totalCount} funds`}
        >
          {totalCount}
        </span>
      )}
    </div>
  )
}
